import {
	fetchApiGetAiChatDetailedList,
	useApiGetAiChatDetailedList,
	type ApiGetAiChatDetailedListQueryParams,
	type Filter,
} from '@repo/api-codegen';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { TimeRange } from '../../../features/AiImpactDashboard/types';
import type { IBaseModel } from '../../types';
import { processSentimentFilter } from './chatUtils';

export * from './chatUtils';

export interface IAiChat extends IBaseModel {
	title: string | null;
	prompt: string[];
	user_id: string;
	user_name?: string | null;
	user_profile_picture?: string | null;
	score?: number | null;
	status: string;
	messages_count: number;
	queried_resources_count: number;
	queried_resource_types: string[];
	queried_resource_details: Array<{
		type: string;
		name: string;
		icon: string;
		integration_name?: string;
		integration_icon?: string;
		integration_type?: string;
	}>;
	time_saved_hours?: number | null;
	complexity?: string | null;
	system_rating?: string | null;
	time_savings_categories?: string[] | null;
	time_savings_breakdown?: Record<string, number>;
	sentiments?: (string | null)[];
	persona_id?: string | null;
	persona_name?: string | null;
	persona_avatar_url?: string | null;
}

interface AiChatFilters {
	sort?: string | { field: string; order: string };
	time_range?: TimeRange;
	filter?: Filter;
	search_term?: string;
}

interface UseAiChatListArgs {
	page?: number;
	filters?: AiChatFilters;
	options?: Record<string, unknown>;
}

interface UseAiChatInfiniteListArgs {
	filters?: AiChatFilters;
	options?: Record<string, unknown>;
}

const getSortBy = (
	sort?: string | { field: string; order: string }
): 'created_at' | 'time_saved_hours' => {
	if (typeof sort === 'string') {
		return sort === 'time_saved_hours' ? 'time_saved_hours' : 'created_at';
	}
	return sort?.field === 'time_saved_hours' ? 'time_saved_hours' : 'created_at';
};

const getSortOrder = (
	sort?: string | { field: string; order: string }
): 'oldest' | 'newest' => {
	// These are the only sortable fields for AI Chats
	const sortableFields = ['created_at', 'prompt', 'time_saved_hours'];

	// sort being a string is a legacy case, this is here for backwards compatibility
	if (typeof sort === 'string') {
		return sort === 'created_at' || sort === 'prompt' ? 'oldest' : 'newest';
	}

	// oldest = ascending, newest = descending
	if (sort?.field && sortableFields.includes(sort.field)) {
		return sort.order === 'asc' ? 'oldest' : 'newest';
	}

	return 'newest';
};

export function useAiChatList(args: UseAiChatListArgs = {}) {
	const page = args.page || 1;
	const filters = args.filters || {};
	const options = args.options || {};

	const page_size = 1000;
	const sort_order = getSortOrder(filters?.sort);
	const sort_by = getSortBy(filters?.sort);
	const time_range: TimeRange = (filters?.time_range as TimeRange) || '365d';

	const catalogFilter = filters?.filter;
	const { sentimentFilter, filterWithoutSentiment } =
		processSentimentFilter(catalogFilter);

	const serializedFilter =
		filterWithoutSentiment && typeof filterWithoutSentiment === 'object'
			? JSON.stringify(filterWithoutSentiment)
			: undefined;
	const searchTerm =
		typeof filters?.search_term === 'string' ? filters.search_term : undefined;

	const queryParams: ApiGetAiChatDetailedListQueryParams = {
		page,
		limit: page_size,
		offset: (page - 1) * page_size,
		sort_order,
		sort_by,
		time_range,
		filter: serializedFilter,
		search_term: searchTerm,
	};

	if (sentimentFilter) {
		queryParams.sentiment_filter = sentimentFilter;
	}

	return useApiGetAiChatDetailedList(
		{
			queryParams,
		},
		{
			placeholderData: keepPreviousData,
			refetchOnWindowFocus: false,
			...options,
		}
	);
}

export function useAiChatInfiniteList({
	filters = {},
	options = {},
}: UseAiChatInfiniteListArgs = {}) {
	const sort_order = getSortOrder(filters?.sort);
	const sort_by = getSortBy(filters?.sort);
	const time_range: TimeRange = (filters?.time_range as TimeRange) || '365d';

	const catalogFilter = filters?.filter;
	const { sentimentFilter, filterWithoutSentiment } =
		processSentimentFilter(catalogFilter);

	const serializedFilter =
		filterWithoutSentiment && typeof filterWithoutSentiment === 'object'
			? JSON.stringify(filterWithoutSentiment)
			: undefined;
	const searchTerm =
		typeof filters?.search_term === 'string' ? filters.search_term : undefined;

	return useInfiniteQuery({
		queryKey: [
			'ai-chats',
			'infinite',
			sort_order,
			sort_by,
			time_range,
			serializedFilter ?? null,
			searchTerm ?? null,
			sentimentFilter ?? null,
		],
		queryFn: async ({ pageParam = 1 }) => {
			const page = pageParam as number;
			const limit = 50;
			const offset = (page - 1) * limit;

			const queryParams: ApiGetAiChatDetailedListQueryParams = {
				page,
				limit,
				offset,
				sort_order,
				sort_by,
				time_range,
				filter: serializedFilter,
				search_term: searchTerm,
			};

			if (sentimentFilter) {
				queryParams.sentiment_filter = sentimentFilter;
			}

			return await fetchApiGetAiChatDetailedList({
				queryParams,
			});
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			if (lastPage?.meta?.next_page) {
				return lastPage.meta.next_page as number;
			}
			return undefined;
		},
		placeholderData: keepPreviousData,
		select: (data) => {
			const flat_results = data.pages.flatMap((page) => page.results || []);
			// Deduplicate by ID to handle page drift: prevents items from appearing again on next page if new DB entries shift the result set
			const seen = new Set<string>();
			const deduplicated_results = flat_results.filter((r) => {
				if (seen.has(r.id)) {
					return false;
				}
				seen.add(r.id);
				return true;
			});
			return {
				pages: deduplicated_results,
				pageParams: data.pageParams,
				count: data.pages[data.pages.length - 1]?.count || 0,
			};
		},
		...options,
	});
}
